import React, { useEffect, useState } from 'react';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import { FaTrashAlt, FaInfoCircle, FaPlus, FaShieldAlt, FaFilter, FaMapMarkerAlt, FaGlobe } from "react-icons/fa";
import { FiRefreshCw } from 'react-icons/fi';
import { AiOutlineClockCircle } from 'react-icons/ai';
import Calling from '../../api/Calling';
import formatDate from '../../components/formatDate';
import generateCaptcha from '../../components/captcha';

const RateLimit = () => {
    const [loader, setLoader] = useState(false);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedBlocked, setSelectedBlocked] = useState([]);
    const [newWhitelist, setNewWhitelist] = useState({ ip: '', reason: '' });
    const [viewInfo, setViewInfo] = useState(null);
    const [ipDetail, setIpDetail] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [filters, setFilters] = useState({
        type: 'all', // all, whitelist, blocked
        status: 'all', // all, active, blocked
        searchIP: '',
        searchEndpoint: ''
    });

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getRateLimitEvents();
            if (resp?.status) {
                const sorted = resp.data.sort((a, b) => {
                    if (a.isWhitelisted && !b.isWhitelisted) return -1;
                    if (!a.isWhitelisted && b.isWhitelisted) return 1;
                    return new Date(b.blockedAt) - new Date(a.blockedAt);
                });
                setData(sorted);
                setFilteredData(sorted);
                setSelectedBlocked([]);
                setLastUpdated(new Date());
            } else toast.error(resp?.message);
        } catch {
            toast.error("Failed to fetch data");
        } finally {
            setLoader(false);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            const resp = await Calling.getRateLimitEvents();
            if (resp?.status) {
                const sorted = resp.data.sort((a, b) => {
                    if (a.isWhitelisted && !b.isWhitelisted) return -1;
                    if (!a.isWhitelisted && b.isWhitelisted) return 1;
                    return new Date(b.blockedAt) - new Date(a.blockedAt);
                });
                setData(sorted);
                setFilteredData(sorted);
                setSelectedBlocked([]);
                setLastUpdated(new Date());
                // toast.success("Data refreshed successfully");
            } else toast.error(resp?.message);
        } catch {
            toast.error("Failed to refresh data");
        } finally {
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Apply filters
    useEffect(() => {
        let filtered = [...data];

        // Filter by type
        if (filters.type === 'whitelist') {
            filtered = filtered.filter(r => r.isWhitelisted);
        } else if (filters.type === 'blocked') {
            filtered = filtered.filter(r => !r.isWhitelisted);
        }

        // Filter by status
        if (filters.status === 'active') {
            filtered = filtered.filter(r => !r.status);
        } else if (filters.status === 'blocked') {
            filtered = filtered.filter(r => r.status);
        }

        // Filter by IP
        if (filters.searchIP) {
            filtered = filtered.filter(r =>
                r.ip.toLowerCase().includes(filters.searchIP.toLowerCase())
            );
        }

        // Filter by Endpoint
        if (filters.searchEndpoint) {
            filtered = filtered.filter(r =>
                r.url?.toLowerCase().includes(filters.searchEndpoint.toLowerCase())
            );
        }

        setFilteredData(filtered);
    }, [filters, data]);

    const handleSelectBlocked = (id) => {
        if (selectedBlocked.includes(id))
            setSelectedBlocked(selectedBlocked.filter(rid => rid !== id));
        else setSelectedBlocked([...selectedBlocked, id]);
    };

    const handleDelete = async (id) => {
        const ids = id ? [id] : selectedBlocked;
        if (!ids.length) return toast.info('No records selected');
        const content = `Are you sure you want to delete ${ids.length} record(s)?`;
        const check = generateCaptcha(content);
        if (!check) return toast.error('Answer is not correct', { autoClose: 2000 });

        setLoader(true);
        try {
            // Pass ids array in body: { ids: ["id1", "id2", ...] }
            const resp = await Calling.deleteRateLimitEvent({ ids });
            if (resp?.status) {
                toast.success(resp.message || 'Deleted successfully!');
                setSelectedBlocked([]);
                fetchData();
            } else {
                toast.error(resp?.message || 'Failed to delete');
            }
        } catch {
            toast.error('Failed to delete');
        } finally {
            setLoader(false);
        }
    };

    const handleStatusToggle = async (record) => {
        if (record.isWhitelisted) return;
        setLoader(true);
        try {
            const resp = await Calling.changeRateLimitStatus(record._id, !record.status);
            if (resp?.status) toast.success(resp.message || 'Status updated!');
            else toast.error(resp.message);
            fetchData();
        } catch {
            toast.error('Failed to update status');
        } finally {
            setLoader(false);
        }
    };

    const handleAddWhitelist = async () => {
        if (!newWhitelist.ip) return toast.error('IP is required');
        setLoader(true);
        try {
            const resp = await Calling.addWhitelist(newWhitelist.ip, newWhitelist.reason);
            if (resp?.status) {
                toast.success('IP added to whitelist!');
                setNewWhitelist({ ip: '', reason: '' });
                fetchData();
            } else toast.error(resp.message);
        } catch {
            toast.error('Failed to add whitelist');
        } finally {
            setLoader(false);
        }
    };

    const handleRemoveWhitelist = async (ip) => {
        setLoader(true);
        try {
            const resp = await Calling.removeWhitelist(ip);
            if (resp?.status) toast.success('IP removed from whitelist!');
            else toast.error(resp.message);
            fetchData();
        } catch {
            toast.error('Failed to remove whitelist');
        } finally {
            setLoader(false);
        }
    };

    const getEndpointName = (url) => {
        if (!url) return '-';
        const parts = url.split('/');
        return parts[parts.length - 1] || parts[parts.length - 2] || url;
    };

    const getUserAgentType = (userAgent) => {
        if (!userAgent || userAgent === '-') return '-';
        if (userAgent.toLowerCase().includes('postman')) return 'Postman';
        if (userAgent.toLowerCase().includes('chrome')) return 'Chrome';
        if (userAgent.toLowerCase().includes('firefox')) return 'Firefox';
        if (userAgent.toLowerCase().includes('safari')) return 'Safari';
        if (userAgent.toLowerCase().includes('mobile')) return 'Mobile';
        return 'Other';
    };

    const whitelistCount = data.filter(r => r.isWhitelisted).length;
    const blockedCount = data.filter(r => !r.isWhitelisted).length;

    // Handle select all checkbox
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allBlockedIds = filteredData
                .filter(r => !r.isWhitelisted)
                .map(r => r._id);
            setSelectedBlocked(allBlockedIds);
        } else {
            setSelectedBlocked([]);
        }
    };

    const handleCheckIpDetail = async (ip) => {
        setLoader(true);
        try {
            const resp = await Calling.getIpToDetail(ip);
            console.log(resp);
            if (resp?.status) {
                setIpDetail(resp?.data);
            } else {
                toast.error(resp?.message || "Failed to fetch IP details");
            }
        } catch {
            toast.error("Failed to fetch IP details");
        } finally {
            setLoader(false);
        }
    };

    // Check if all visible blocked items are selected
    const allBlockedInFilterSelected = () => {
        const blockedInFilter = filteredData.filter(r => !r.isWhitelisted);
        return blockedInFilter.length > 0 && blockedInFilter.every(r => selectedBlocked.includes(r._id));
    };

    // Check if some (but not all) blocked items are selected
    const someBlockedSelected = () => {
        const blockedInFilter = filteredData.filter(r => !r.isWhitelisted);
        return selectedBlocked.length > 0 && !allBlockedInFilterSelected() && blockedInFilter.some(r => selectedBlocked.includes(r._id));
    };

    if (loader) return <Loader />
    return (
        <>
            <div className="w-full">
                {/* Header */}
                <div className="bg-main-white rounded-xl shadow-lg p-4 sm:p-6 mb-6">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-2 gap-3">
                        {/* Title with Icon and Badge */}
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl font-bold flex items-center gap-3 text-main-font">
                                <div className="relative p-2.5 bg-main-blue rounded-xl shadow-lg">
                                    <FaShieldAlt className="text-white text-xl" />
                                    {data.length > 0 && (
                                        <span className="absolute -top-2 -right-2 min-w-[20px] h-5 bg-main-exBlue text-white text-[10px] font-semibold rounded-full flex items-center justify-center shadow-md">
                                            {data.length > 99 ? '99+' : data.length}
                                        </span>
                                    )}
                                </div>
                                <span>Rate Limit Management</span>
                            </h1>
                        </div>

                        {/* Actions - Last Updated & Refresh */}
                        <div className="flex items-center gap-3">
                            <div className="bg-main-bg px-4 py-2 rounded-lg shadow-sm border border-main-border text-sm text-main-subfont flex items-center gap-2">
                                <AiOutlineClockCircle className="text-main-subfont" size={16} />
                                <span>Last updated: {lastUpdated.toLocaleTimeString()}</span>
                            </div>
                            <button
                                onClick={handleRefresh}
                                disabled={isRefreshing}
                                className={`bg-main-blue hover:bg-main-blue/80 text-white px-4 py-2 rounded-lg shadow-sm border border-main-blue text-sm font-medium flex items-center gap-2 transition-all duration-200 ${isRefreshing ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-md'}`}
                                title="Refresh Data"
                            >
                                <FiRefreshCw className={`${isRefreshing ? 'animate-spin' : ''}`} size={16} />
                            </button>
                        </div>
                    </div>
                    <p className="text-main-subfont mt-2">Monitor and manage IP rate limits and whitelist settings</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-5 rounded-xl shadow-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-blue-100 text-sm font-medium">Total Records</p>
                                <p className="text-3xl font-bold mt-1">{data.length}</p>
                            </div>
                            <FaShieldAlt className="text-4xl opacity-80" />
                        </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-5 rounded-xl shadow-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-green-100 text-sm font-medium">Whitelisted IPs</p>
                                <p className="text-3xl font-bold mt-1">{whitelistCount}</p>
                            </div>
                            <FaShieldAlt className="text-4xl opacity-80" />
                        </div>
                    </div>
                    <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-5 rounded-xl shadow-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-red-100 text-sm font-medium">Blocked IPs</p>
                                <p className="text-3xl font-bold mt-1">{blockedCount}</p>
                            </div>
                            <FaShieldAlt className="text-4xl opacity-80" />
                        </div>
                    </div>
                </div>

                {/* Add Whitelist Card */}
                <div className="bg-main-white rounded-xl shadow-md p-6 mb-6 border border-main-border">
                    <h2 className="text-lg font-semibold text-main-font mb-4 flex items-center gap-2">
                        <FaPlus className="text-main-blue" />
                        Add IP to Whitelist
                    </h2>
                    <div className="flex flex-col md:flex-row gap-3">
                        <input
                            type="text"
                            placeholder="Enter IP Address (e.g., 192.168.1.1)"
                            value={newWhitelist.ip}
                            onChange={(e) => setNewWhitelist({ ...newWhitelist, ip: e.target.value })}
                            className='border border-main-border p-3 rounded-lg flex-1 focus:ring-2 focus:ring-main-blue focus:border-transparent outline-none transition'
                        />
                        <input
                            type="text"
                            placeholder="Reason for whitelisting (optional)"
                            value={newWhitelist.reason}
                            onChange={(e) => setNewWhitelist({ ...newWhitelist, reason: e.target.value })}
                            className='border border-main-border p-3 rounded-lg flex-1 focus:ring-2 focus:ring-main-blue focus:border-transparent outline-none transition'
                        />
                        <button
                            onClick={handleAddWhitelist}
                            className='bg-main-blue text-white px-6 py-3 rounded-lg hover:bg-main-blue/80 transition font-medium shadow-md hover:shadow-lg flex items-center gap-2 whitespace-nowrap'
                        >
                            <FaPlus />
                            Add Whitelist
                        </button>
                    </div>
                </div>

                {/* Filters Card */}
                <div className="bg-main-white rounded-xl shadow-md p-6 mb-6 border border-main-border">
                    <h2 className="text-lg font-semibold text-main-font mb-4 flex items-center gap-2">
                        <FaFilter className="text-main-exBlue" />
                        Filters
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-main-font mb-2">Type</label>
                            <select
                                value={filters.type}
                                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                                className="w-full border border-main-border p-2.5 rounded-lg focus:ring-2 focus:ring-main-blue focus:border-transparent outline-none"
                            >
                                <option value="all">All</option>
                                <option value="whitelist">Whitelist Only</option>
                                <option value="blocked">Blocked Only</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-main-font mb-2">Status</label>
                            <select
                                value={filters.status}
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                className="w-full border border-main-border p-2.5 rounded-lg focus:ring-2 focus:ring-main-blue focus:border-transparent outline-none"
                            >
                                <option value="all">All</option>
                                <option value="active">Active</option>
                                <option value="blocked">Blocked</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-main-font mb-2">Search IP</label>
                            <input
                                type="text"
                                placeholder="Filter by IP..."
                                value={filters.searchIP}
                                onChange={(e) => setFilters({ ...filters, searchIP: e.target.value })}
                                className="w-full border border-main-border p-2.5 rounded-lg focus:ring-2 focus:ring-main-blue focus:border-transparent outline-none"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-main-font mb-2">Search Endpoint</label>
                            <input
                                type="text"
                                placeholder="Filter by endpoint..."
                                value={filters.searchEndpoint}
                                onChange={(e) => setFilters({ ...filters, searchEndpoint: e.target.value })}
                                className="w-full border border-main-border p-2.5 rounded-lg focus:ring-2 focus:ring-main-blue focus:border-transparent outline-none"
                            />
                        </div>
                    </div>
                    {(filters.type !== 'all' || filters.status !== 'all' || filters.searchIP || filters.searchEndpoint) && (
                        <button
                            onClick={() => setFilters({ type: 'all', status: 'all', searchIP: '', searchEndpoint: '' })}
                            className="mt-4 text-sm text-main-exBlue hover:text-main-blue font-medium"
                        >
                            Clear All Filters
                        </button>
                    )}
                </div>

                {/* Table Card */}
                <div className="bg-main-white rounded-xl shadow-md border border-main-border overflow-hidden">
                    <div className="p-6 border-b border-main-border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-lg font-semibold text-main-font">IP Records</h2>
                            <p className="text-sm text-main-subfont mt-1">
                                Showing {filteredData.length} of {data.length} records
                            </p>
                        </div>
                        {selectedBlocked.length > 0 && (
                            <button
                                onClick={() => handleDelete(null)}
                                className="bg-main-red text-white px-4 py-2 rounded-lg hover:bg-main-red/80 transition font-medium shadow-md hover:shadow-lg flex items-center gap-2"
                            >
                                <FaTrashAlt />
                                Delete Selected ({selectedBlocked.length})
                            </button>
                        )}
                    </div>

                    <div className="overflow-x-auto">
                        <table className='w-full'>
                            <thead className='bg-main-th border-b-2 border-main-border'>
                                <tr>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider w-12">
                                        <input
                                            type="checkbox"
                                            checked={allBlockedInFilterSelected()}
                                            ref={input => {
                                                if (input) {
                                                    input.indeterminate = someBlockedSelected();
                                                }
                                            }}
                                            onChange={handleSelectAll}
                                            className="w-4 h-4 text-main-blue rounded focus:ring-2 focus:ring-main-blue cursor-pointer"
                                            title="Select all blocked IPs"
                                        />
                                    </th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider w-16">#</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">IP Address</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Endpoint</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Full URL</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Reason</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">User Agent</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Blocked At</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Count</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Type</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Status</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Info</th>
                                    <th className="px-4 py-4 text-left text-xs font-semibold text-main-font uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-main-border">
                                {filteredData.length ? filteredData.map((r, idx) => (
                                    <tr key={r._id} className="hover:bg-main-th transition">
                                        <td className="px-4 py-4">
                                            {!r.isWhitelisted && (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedBlocked.includes(r._id)}
                                                    onChange={() => handleSelectBlocked(r._id)}
                                                    className="w-4 h-4 text-main-blue rounded focus:ring-2 focus:ring-main-blue cursor-pointer"
                                                />
                                            )}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont font-medium">{idx + 1}</td>
                                        <td className="px-4 py-4 text-sm font-mono text-main-font">
                                            <div className="flex items-center gap-2">
                                                {r.ip}
                                                <button
                                                    onClick={() => handleCheckIpDetail(r.ip)}
                                                    className="text-main-blue hover:text-main-blue/80 transition"
                                                    title="Check IP Location"
                                                >
                                                    <FaGlobe />
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-sm">
                                            <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-xs font-semibold">
                                                {getEndpointName(r.url)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont max-w-xs truncate" title={r.url}>
                                            {r.url || '-'}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont max-w-xs truncate">{r.reason}</td>
                                        <td className="px-4 py-4 text-sm">
                                            <span className="bg-main-yellow/20 text-yellow-800 px-2 py-1 rounded-md text-xs font-semibold">
                                                {getUserAgentType(r.userInfo?.userAgent)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-main-subfont whitespace-nowrap">
                                            {formatDate(r.blockedAt)}
                                        </td>
                                        <td className="px-4 py-4 text-sm">
                                            <span className="bg-main-exBlue/20 text-main-exBlue px-2 py-1 rounded-full text-xs font-semibold">{r.count}</span>
                                        </td>
                                        <td className="px-4 py-4 text-sm">
                                            {r.isWhitelisted ? (
                                                <span className="bg-main-thHover text-main-blue px-3 py-1 rounded-full text-xs font-semibold">Whitelist</span>
                                            ) : (
                                                <span className="bg-red-100 text-main-red px-3 py-1 rounded-full text-xs font-semibold">Blocked</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            {!r.isWhitelisted && (
                                                <button
                                                    className={`px-3 py-1 rounded-lg text-xs font-semibold transition ${r.status
                                                        ? 'bg-red-100 text-main-red hover:bg-red-200'
                                                        : 'bg-main-thHover text-main-blue hover:bg-main-th'
                                                        }`}
                                                    onClick={() => handleStatusToggle(r)}
                                                >
                                                    {r.status ? 'Blocked' : 'Active'}
                                                </button>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            {r.userInfo && (
                                                <button
                                                    className="text-main-exBlue hover:text-main-blue transition text-lg"
                                                    onClick={() => setViewInfo(r.userInfo)}
                                                    title="View Device Info"
                                                >
                                                    <FaInfoCircle />
                                                </button>
                                            )}
                                        </td>
                                        <td className="px-4 py-4">
                                            {r.isWhitelisted ? (
                                                <button
                                                    onClick={() => handleRemoveWhitelist(r.ip)}
                                                    className='text-main-red hover:text-main-red/80 transition text-lg'
                                                    title="Remove from whitelist"
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handleDelete(r._id)}
                                                    className='text-main-red hover:text-main-red/80 transition text-lg'
                                                    title="Delete record"
                                                >
                                                    <FaTrashAlt />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="13" className='text-center py-12 text-main-subfont'>
                                            <div className="flex flex-col items-center gap-2">
                                                <FaShieldAlt className="text-4xl text-main-border" />
                                                <p className="font-medium">No records found</p>
                                                {(filters.type !== 'all' || filters.status !== 'all' || filters.searchIP || filters.searchEndpoint) && (
                                                    <button
                                                        onClick={() => setFilters({ type: 'all', status: 'all', searchIP: '', searchEndpoint: '' })}
                                                        className="text-main-exBlue hover:text-main-blue text-sm mt-2"
                                                    >
                                                        Clear filters to see all records
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* User Info Modal */}
                {viewInfo && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                        <div className="bg-main-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden">
                            <div className="bg-main-blue text-white p-6 flex justify-between items-center">
                                <h2 className="text-2xl font-bold flex items-center gap-2">
                                    <FaInfoCircle />
                                    Device / User Information
                                </h2>
                                <button
                                    onClick={() => setViewInfo(null)}
                                    className="text-white hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center transition font-bold text-2xl"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto max-h-[calc(80vh-100px)]">
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-main-bg p-4 rounded-lg">
                                            <p className="text-xs text-main-subfont font-semibold uppercase mb-1">IP Address</p>
                                            <p className="text-sm font-mono text-main-font">{viewInfo.ip || '-'}</p>
                                        </div>
                                        <div className="bg-main-bg p-4 rounded-lg">
                                            <p className="text-xs text-main-subfont font-semibold uppercase mb-1">Method</p>
                                            <p className="text-sm font-semibold text-main-font">{viewInfo.method || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="bg-main-bg p-4 rounded-lg">
                                        <p className="text-xs text-main-subfont font-semibold uppercase mb-1">URL</p>
                                        <p className="text-sm text-main-font break-all">{viewInfo.url || '-'}</p>
                                    </div>
                                    <div className="bg-main-bg p-4 rounded-lg">
                                        <p className="text-xs text-main-subfont font-semibold uppercase mb-1">User Agent</p>
                                        <p className="text-sm text-main-font break-all">{viewInfo.userAgent || '-'}</p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-main-bg p-4 rounded-lg">
                                            <p className="text-xs text-main-subfont font-semibold uppercase mb-1">Platform</p>
                                            <p className="text-sm text-main-font">{viewInfo.platform || '-'}</p>
                                        </div>
                                        <div className="bg-main-bg p-4 rounded-lg">
                                            <p className="text-xs text-main-subfont font-semibold uppercase mb-1">Device Model</p>
                                            <p className="text-sm text-main-font">{viewInfo.deviceModel || '-'}</p>
                                        </div>
                                    </div>
                                    <div className="bg-main-bg p-4 rounded-lg">
                                        <p className="text-xs text-main-subfont font-semibold uppercase mb-1">App Version</p>
                                        <p className="text-sm text-main-font">{viewInfo.appVersion || '-'}</p>
                                    </div>
                                    <div className="bg-main-th p-4 rounded-lg border border-main-blue/30">
                                        <p className="text-xs text-main-blue font-semibold uppercase mb-2">Raw JSON</p>
                                        <pre className="text-xs text-main-font overflow-x-auto">
                                            {JSON.stringify(viewInfo, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* IP Detail Modal */}
                {ipDetail && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
                        <div className="bg-main-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden">
                            <div className="bg-main-blue text-white p-6 flex justify-between items-center">
                                <h2 className="text-2xl font-bold flex items-center gap-2">
                                    <FaMapMarkerAlt />
                                    IP Location Details
                                </h2>
                                <button
                                    onClick={() => setIpDetail(null)}
                                    className="text-white hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center transition font-bold text-2xl"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)]">
                                <div className="space-y-4">
                                    {/* IP Address Header */}
                                    <div className="bg-main-bg p-4 rounded-xl border border-main-border text-center">
                                        <p className="text-xs text-main-subfont font-semibold uppercase mb-1">IP Address</p>
                                        <p className="text-xl font-mono font-bold text-main-font">{ipDetail.query || '-'}</p>
                                    </div>

                                    {/* Location Info */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-main-th p-4 rounded-xl border border-main-blue/20">
                                            <p className="text-xs text-main-blue font-semibold uppercase mb-1">Country</p>
                                            <p className="text-sm font-semibold text-main-font flex items-center gap-2">
                                                <span className="text-lg">{ipDetail.countryCode === 'IN' ? '🇮🇳' : '🌍'}</span>
                                                {ipDetail.country || '-'}
                                            </p>
                                            <p className="text-xs text-main-subfont mt-1">{ipDetail.countryCode || '-'}</p>
                                        </div>
                                        <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                                            <p className="text-xs text-purple-600 font-semibold uppercase mb-1">Region</p>
                                            <p className="text-sm font-semibold text-main-font">{ipDetail.regionName || '-'}</p>
                                            <p className="text-xs text-main-subfont mt-1">{ipDetail.region || '-'}</p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="bg-orange-50 p-4 rounded-xl border border-orange-100">
                                            <p className="text-xs text-orange-600 font-semibold uppercase mb-1">City</p>
                                            <p className="text-sm font-semibold text-main-font">{ipDetail.city || '-'}</p>
                                        </div>
                                        <div className="bg-teal-50 p-4 rounded-xl border border-teal-100">
                                            <p className="text-xs text-teal-600 font-semibold uppercase mb-1">ZIP Code</p>
                                            <p className="text-sm font-semibold text-main-font">{ipDetail.zip || '-'}</p>
                                        </div>
                                    </div>

                                    {/* Coordinates */}
                                    <div className="bg-main-exBlue/10 p-4 rounded-xl border border-main-exBlue/20">
                                        <p className="text-xs text-main-exBlue font-semibold uppercase mb-2">Coordinates</p>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-xs text-main-subfont">Latitude</p>
                                                <p className="text-sm font-mono font-semibold text-main-font">{ipDetail.lat || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-main-subfont">Longitude</p>
                                                <p className="text-sm font-mono font-semibold text-main-font">{ipDetail.lon || '-'}</p>
                                            </div>
                                            <a
                                                href={`https://www.google.com/maps?q=${ipDetail.lat},${ipDetail.lon}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="bg-main-exBlue text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-main-exBlue/80 transition flex items-center gap-1"
                                            >
                                                <FaMapMarkerAlt />
                                                View Map
                                            </a>
                                        </div>
                                    </div>

                                    {/* Timezone */}
                                    <div className="bg-main-yellow/20 p-4 rounded-xl border border-main-yellow/30">
                                        <p className="text-xs text-yellow-700 font-semibold uppercase mb-1">Timezone</p>
                                        <p className="text-sm font-semibold text-main-font">{ipDetail.timezone || '-'}</p>
                                    </div>

                                    {/* ISP Info */}
                                    <div className="bg-main-bg p-4 rounded-xl border border-main-border">
                                        <p className="text-xs text-main-subfont font-semibold uppercase mb-3">Network Information</p>
                                        <div className="space-y-3">
                                            <div>
                                                <p className="text-xs text-main-subfont">ISP</p>
                                                <p className="text-sm font-semibold text-main-font">{ipDetail.isp || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-main-subfont">Organization</p>
                                                <p className="text-sm font-semibold text-main-font">{ipDetail.org || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-main-subfont">AS Number</p>
                                                <p className="text-sm font-mono text-main-font">{ipDetail.as || '-'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Raw JSON */}
                                    <div className="bg-main-th p-4 rounded-xl border border-main-blue/30">
                                        <p className="text-xs text-main-blue font-semibold uppercase mb-2">Raw JSON</p>
                                        <pre className="text-xs text-main-font overflow-x-auto bg-main-white p-3 rounded-lg">
                                            {JSON.stringify(ipDetail, null, 2)}
                                        </pre>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default RateLimit;